import React from 'react';
import {
  useAutomationMessageStore,
  useClientExecutiveStore,
  useCountryStore,
  useCourseStore,
  useCurriculumStore,
  useCustomFieldStore,
  useFacilityStore,
  useFlagDefinitionStore,
  useHolidayAllRegionsStore,
  useHolidayStore,
  useInstructorStore,
  useKlassStore,
  useLanguageStore,
  usePartnerStore,
  usePipelineKlassStore,
  usePrivateCourseStore,
  useRedHatRegionStore,
  useSavedFilterStore,
  useTrainingSpecialistStrategicStore,
} from '@app/store/entityStore';
import { useCommunicationStore } from '@app/store/communicationStore';
import { useParameterStore } from '@app/store/parameterStore';
import { useAuthenticatedSocket } from '@app/hooks/useAuthenticatedSocket';
import { KLASS_INSTRUCTOR_FIELDS } from '@app/model/Klass';

/**
 * Structural view of a store, narrow enough that every entity store satisfies it
 * regardless of its item type. Avoids fighting the generics of the zustand hooks.
 */
type SyncableStore = {
  getState: () => {
    isLoaded: boolean;
    syncItem: (raw: any) => void;
    removeItem: (id: string | number) => void;
  };
};

/**
 * Entity name (the model interface name, as sent by the server) -> owning store.
 * Adding a synced entity means adding one line here; the store carries its own
 * ingestor, so no ingestion wiring is needed at this layer.
 */
const ENTITY_STORES: Record<string, SyncableStore> = {
  AutomationMessage: useAutomationMessageStore,
  ClientExecutive: useClientExecutiveStore,
  Country: useCountryStore,
  Course: useCourseStore,
  Curriculum: useCurriculumStore,
  CustomField: useCustomFieldStore,
  Facility: useFacilityStore,
  FlagDefinition: useFlagDefinitionStore,
  Holiday: useHolidayStore,
  Instructor: useInstructorStore,
  Klass: useKlassStore,
  Language: useLanguageStore,
  Partner: usePartnerStore,
  // A pipeline request is not a class, so it has its own store rather than joining the
  // Klass fan-in below.
  PipelineKlass: usePipelineKlassStore,
  PrivateCourse: usePrivateCourseStore,
  RedHatRegion: useRedHatRegionStore,
  SavedFilter: useSavedFilterStore,
  TrainingSpecialistStrategic: useTrainingSpecialistStrategicStore,

  // Polymorphic Klass hierarchy — the server names the concrete subclass,
  // but every variant lives in the single klass store. AdHocKlass is an
  // OpenEnrolmentKlass, and the server names the concrete class, so it needs
  // its own line here.
  OpenEnrolmentKlass: useKlassStore,
  AdHocKlass: useKlassStore,
  PrivateKlass: useKlassStore,
};

/**
 * Entity-specific fan-out, for entities that other stores denormalize.
 * Runs after the entity's own store has been synced.
 */
const CASCADES: Record<string, (payload: any, action: string) => void> = {
  /**
   * Holidays are held twice: nationwide ones for the Calendar, and every subdivision's for the
   * administration screen that manages them. ENTITY_STORES above names the first, so a push has
   * to be carried to the second by hand or an administrator watches a list go stale under them.
   *
   * Either store ignores the push when its own cache is cold, which is the usual case for one of
   * them: nothing loads both at once.
   */
  Holiday: (payload, action) => {
    const allRegions = useHolidayAllRegionsStore.getState();

    if (action === 'DELETE') {
      const id = payload && typeof payload === 'object' ? payload.id : payload;
      if (id !== undefined && id !== null) allRegions.removeItem(id);
      return;
    }

    allRegions.syncItem(payload);

    // A holiday that has just been given a region is no longer nationwide, and the nationwide
    // store's ingestor drops it rather than applying it - which leaves the row as it was before
    // the edit. Take it out, or the Calendar keeps drawing a day the country no longer observes.
    if (payload?.region && payload.id !== undefined) {
      useHolidayStore.getState().removeItem(payload.id);
    }
  },

  Instructor: (payload, action) => {
    if (action !== 'UPDATE') return;

    const klassStore = useKlassStore.getState();
    if (!klassStore.isLoaded) return;

    // Take the ingested instructor back out of its store so klasses reference the
    // same hydrated object the instructor views use, not the raw payload.
    const instructor =
      useInstructorStore.getState().items.find((i) => i.id === payload.id) ?? payload;

    // A klass holds denormalized Instructor copies in three lists. Ingestion resolves
    // those once at load time, so an instructor update has to be pushed into any
    // already-ingested klass or the UI keeps showing the stale copy.
    let touched = 0;
    const nextItems = klassStore.items.map((klass) => {
      const stale = KLASS_INSTRUCTOR_FIELDS.filter((f) => klass[f]?.some((i) => i?.id === payload.id));
      if (stale.length === 0) return klass;

      touched++;
      const next = { ...klass };
      // Rebuild each list that names this instructor, swapping in the refreshed copy and
      // leaving the others - and the order of the line-up - as they were.
      stale.forEach((f) => {
        next[f] = klass[f].map((i) => (i?.id === payload.id ? instructor : i));
      });
      return next;
    });

    // Only publish a new array if something actually changed — setItems replaces
    // the reference and would otherwise re-render every klass consumer for nothing.
    if (touched > 0) {
      klassStore.setItems(nextItems);
      console.debug(`[WebSocket] Instructor ${payload.id} refreshed in ${touched} klass(es).`);
    }
  },
};

const onMessage = (event: MessageEvent) => {
  const { entity, action, payload } = JSON.parse(event.data);

  if (!entity || !action) {
    console.warn('[WebSocket] Ignoring malformed sync message:', event.data);
    return;
  }

  // Tolerate a fully-qualified class name in case the server ever sends one
  // (the endpoint id had exactly this problem).
  const entityName = String(entity).split(/[.$]/).pop() as string;

  /**
   * A runtime setting changed. Refetched rather than merged from the payload, which is the
   * override *row* — it carries the stored text but not which of the three layers now wins, nor
   * the properties and default values behind it. A reset is worse still: it broadcasts a delete,
   * and the resulting value lives entirely in the layers the row knows nothing about.
   *
   * So the socket is used as an invalidation signal only, and the answer comes from /parameters.
   */
  if (entityName === 'Parameter') {
    useParameterStore.getState().invalidate();
    void useParameterStore.getState().load(true);
    return;
  }

  /**
   * A communication changed. Refetched rather than merged for the same reason a parameter is: the
   * payload is the override *row*, which carries the columns somebody set but not the catalog
   * defaults behind them, nor which fields the row is now answering for. A reset is worse still —
   * it broadcasts a delete, and the resulting wording lives entirely in the layer the row knows
   * nothing about.
   *
   * Only when somebody is looking. Nothing outside the administration screen reads a
   * communication, so a session that has never opened it has an empty cache worth leaving empty.
   */
  if (entityName === 'Communication') {
    if (useCommunicationStore.getState().isLoaded) {
      void useCommunicationStore.getState().load(true);
    }
    return;
  }

  const store = ENTITY_STORES[entityName];
  if (!store) {
    console.warn(`[WebSocket] Unhandled entity sync: ${entity}`);
    return;
  }

  switch (action) {
    case 'DELETE': {
      // A delete may carry the whole entity or just its id.
      const id = payload && typeof payload === 'object' ? payload.id : payload;
      if (id === undefined || id === null) {
        console.warn(`[WebSocket] DELETE for ${entityName} carried no id:`, payload);
        return;
      }
      store.getState().removeItem(id);
      break;
    }

    case 'CREATE':
    case 'UPDATE': {
      if (!payload || typeof payload !== 'object') {
        console.warn(`[WebSocket] ${action} for ${entityName} carried no payload:`, payload);
        return;
      }
      store.getState().syncItem(payload);
      break;
    }

    default:
      console.warn(`[WebSocket] Unhandled action '${action}' for ${entityName}.`);
      return;
  }

  CASCADES[entityName]?.(payload, action);
};

export const EntityUpdateStream: React.FC = () => {
  useAuthenticatedSocket({
    path: '/ws/entity-updates',
    onMessage,
    onOpen: () => {
      // Nothing that happened while the socket was down was delivered, so the parameter cache
      // may be holding a value that has since been changed. Reading it again on every connect
      // closes the window a reconnect would otherwise leave open indefinitely. Cheap: ten-odd
      // rows, and only when a connection is established.
      if (useParameterStore.getState().isLoaded) {
        void useParameterStore.getState().load(true);
      }
      if (useCommunicationStore.getState().isLoaded) {
        void useCommunicationStore.getState().load(true);
      }
    }
  });

  return null;
};